import React, { useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import {
    Box, Typography, Button, Alert, CircularProgress, Stack, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ComparisonView from './ComparisonView';
import ShipmentImageZoom from './ShipmentImage';

const UNZUORDENBAR_KDNR = 15286;

const ShipmentSearchResults = forwardRef(({ shipments, claim, sendMessage, cookies }, ref) => {
    const [approvingId, setApprovingId] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [error, setError] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [viewerIndex, setViewerIndex] = useState(null);

    useImperativeHandle(ref, () => ({
        handleMessage(msg) {
            if (msg.task === 'respApproveClaim') {
                setApprovingId(null);
                if (msg.status === 'succeeded') {
                    setSuccessMsg(`Freigegeben – mpsid: ${msg.newMpsid}`);
                    setError(null); setViewerIndex(null); setConfirmDialog(null);
                } else { setError(msg.message || 'Fehler.'); }
            }
        },
    }));

    const sorted = [...(shipments || [])].sort((a, b) =>
        (a.kdnr === UNZUORDENBAR_KDNR ? 0 : 1) - (b.kdnr === UNZUORDENBAR_KDNR ? 0 : 1)
    );

    const current = viewerIndex !== null ? sorted[viewerIndex] : null;
    const done = successMsg !== null;

    const executeApprove = useCallback((id) => {
        setApprovingId(id); setConfirmDialog(null); setError(null); setSuccessMsg(null);
        sendMessage(JSON.stringify({ task: 'approveClaim', station: cookies.station, staff: cookies.staff, claimId: claim.claimId, shipmentId: id }));
    }, [sendMessage, cookies, claim.claimId]);

    const handleApprove = useCallback((s) => {
        s.kdnr !== UNZUORDENBAR_KDNR ? setConfirmDialog(s) : executeApprove(s.id);
    }, [executeApprove]);

    const nav = (d) => setViewerIndex(p => { const n = p + d; return n < 0 || n >= sorted.length ? p : n; });

    if (!shipments?.length) {
        return <Box sx={{ p: 2 }}><Typography sx={{ color: '#999', fontSize: 13 }}>Keine Übereinstimmungen.</Typography></Box>;
    }

    return (
        <Box>
            {successMsg && <Alert severity="success" sx={{ m: 1.5, borderRadius: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
            {error && <Alert severity="error" sx={{ m: 1.5, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* ── Column-based comparison ── */}
            <ComparisonView
                claim={claim}
                shipments={sorted}
                approvingId={approvingId}
                done={done}
                onApprove={handleApprove}
                onImageClick={(idx) => setViewerIndex(idx)}
            />

            {/* ── Fullscreen image viewer ── */}
            <Dialog open={viewerIndex !== null} onClose={() => setViewerIndex(null)} fullScreen>
                {current && (
                    <>
                        <Box sx={{ px: 4, py: 3, borderBottom: '1px solid #e5e5e3', bgcolor: '#f8f7f4' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                                        Entspricht das Foto diesem Such-Auftrag?
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 4 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: '#999' }}>Kdnr</Typography>
                                            <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: 'monospace' }}>{claim.kdnr}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: '#999' }}>Absender</Typography>
                                            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{claim.absender}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: '#999' }}>Suchbegriffe</Typography>
                                            <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: 'monospace' }}>{(claim.suchbegriffe || []).join(', ')}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                                    <Button variant="contained" size="large" disableElevation
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3 }}
                                        color={current.kdnr === UNZUORDENBAR_KDNR ? 'success' : 'warning'}
                                        startIcon={approvingId === current.id ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                                        onClick={() => handleApprove(current)}
                                        disabled={approvingId !== null || done}>
                                        Freigeben
                                    </Button>
                                    <Button onClick={() => setViewerIndex(null)} size="large" variant="outlined" color="error"
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }} startIcon={<CloseIcon />}>
                                        Schliessen
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                        <Box sx={{
                            display: 'flex', gap: 3, px: 4, py: 1, borderBottom: '1px solid #e5e5e3',
                            bgcolor: current.kdnr === UNZUORDENBAR_KDNR ? '#f0fdf4' : '#fffbeb', alignItems: 'center',
                        }}>
                            <Typography variant="body2"><strong>MPSID:</strong> {current.id}</Typography>
                            <Typography variant="body2"><strong>Kdnr:</strong> {current.kdnr}</Typography>
                            <Typography variant="body2"
                                sx={current.absender?.toLowerCase().includes(claim.absender?.toLowerCase()) ? { color: '#16a34a', fontWeight: 700 } : {}}>
                                <strong>Absender:</strong> {current.absender}
                            </Typography>
                            <Typography variant="body2"
                                sx={(claim.suchbegriffe || []).includes(current.sendungsnummer) ? { color: '#16a34a', fontWeight: 700 } : {}}>
                                <strong>SN:</strong> {current.sendungsnummer}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{viewerIndex + 1} / {sorted.length}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 2, py: 2, overflow: 'hidden', bgcolor: '#fafaf8' }}>
                            <IconButton onClick={() => nav(-1)} disabled={viewerIndex <= 0} size="large"><ArrowBackIcon fontSize="large" /></IconButton>
                            <Box sx={{ flex: 1, height: '100%' }}><ShipmentImageZoom src={current.imageUrl} alt={`Sendung ${current.id}`} /></Box>
                            <IconButton onClick={() => nav(1)} disabled={viewerIndex >= sorted.length - 1} size="large"><ArrowForwardIcon fontSize="large" /></IconButton>
                        </Box>
                    </>
                )}
            </Dialog>

            {/* ── Fremdkunden-Bestätigung ── */}
            <Dialog open={confirmDialog !== null} onClose={() => setConfirmDialog(null)} PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ color: '#d97706', fontWeight: 700, fontSize: 16 }}>Sendung wird entzogen</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Sendung <strong>{confirmDialog?.id}</strong> wird Kunde <strong>{confirmDialog?.kdnr}</strong> entzogen
                        und Kunde <strong>{claim.kdnr}</strong> ({claim.kundenName || '–'}) zugewiesen.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmDialog(null)} sx={{ textTransform: 'none', fontSize: 13 }}>Abbrechen</Button>
                    <Button onClick={() => executeApprove(confirmDialog.id)} color="warning" variant="contained" disableElevation
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 13 }}>Trotzdem freigeben</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

ShipmentSearchResults.displayName = 'ShipmentSearchResults';
export default ShipmentSearchResults;
